import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Axios from 'axios';
import InfiniteScroll from 'react-infinite-scroller';
import SimplePanel from '@/shared/components/SimplePanel';
import { injectIntl, FormattedMessage } from 'react-intl';
import moment from 'moment';
import { FaFileExcel } from 'react-icons/fa';
import LoadingAnimation from '@/shared/components/LoadingAnimation';
import intlShape from '@/shared/utils/intlShape';
import api from '@/shared/api';
import config from '@/config';
import { showWhenPermission } from '@/shared/components/security';
import { injectStateService } from '@/shared/services/StateService';
import ZendeskHelp from '@/shared/components/ZendeskHelp';
import ScrollArrow from '@/shared/components/ScrollArrow';
import handleHttpError from '@/shared/services/HandleHttpError';
import DataCacheService from '@/shared/services/DataCacheService';
import Button from '@/shared/components/Button';
import SignalRService from '@/shared/services/SignalRService';
import ToastrService from '@/shared/services/ToastrService';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import AuthService from '@/shared/services/AuthService';
import RegistrationsPerReportFilters, {
  defaultFilters,
  getParamsFromFilters,
} from '../RegistrationsPerReportFilters';
import messages from '../../reportingMessages';
import ExportToExcelDropdown from '../ExportToExcelDropdown';

import RegistrationsPerRow from './RegistrationsPerRow';
import { getVisibleColumnsFromTotals } from '../helpers';

const SessionStorageKey = 'RegistrationsPerRelationReportParams';
const ShowHoursStorageKey = 'RegistrationsPerRelationReportShowHours';

class RegistrationsPerRelationContainer extends Component {
  state = {
    hasMoreData: false,
    loading: null,
    pagingKey: 1,
    totals: null,
    rows: [],
    showHours: false,
    reportFilters: {},
    exporting: false,
  };

  constructor(props) {
    super(props);
    const { searchParams = {} } = props;
    const sessionParams = JSON.parse(sessionStorage.getItem(SessionStorageKey)) || {};

    if (sessionParams.dateFrom) {
      sessionParams.dateFrom = moment(sessionParams.dateFrom);
    }
    if (sessionParams.dateUntil) {
      sessionParams.dateUntil = moment(sessionParams.dateUntil);
    }

    this.state = {
      ...this.state,
      reportFilters: {
        ...defaultFilters,
        ...sessionParams,
        ...searchParams,
      },
      hasMoreData: true,
      showHours: !!sessionStorage.getItem(ShowHoursStorageKey),
    };
  }

  componentDidMount() {
    const { intl } = this.props;

    api.base.get(`${config.appUrls.tasks}/api/TaskTemplates`).then(({ data }) => {
      const translatedTemplates = data
        .map(t => ({
          ...t,
          label: intl.formatMessage(listItemMsg('taskTemplate', t.id, t.name)), // Map translation to label property
        }))
        .sort((a, b) => (a.label > b.label) - (a.label < b.label)); // Sort on label property

      this.setState(state => ({
        ...state,
        mainTaskTemplates: translatedTemplates,
      }));
    });
  }
  componentWillUnmount() {
    if (this.reportToken && !this.reportToken.token.reason) {
      this.reportToken.cancel('Operation canceled due to leaving page.');
    }
  }

  getParams() {
    const { reportFilters = {} } = this.state;
    return getParamsFromFilters(reportFilters);
  }

  retrieve = page => {
    const { reportFilters = {}, pagingKey } = this.state;

    if (this.reportToken && !this.reportToken.token.reason) {
      this.reportToken.cancel('Operation canceled due to new request.');
    }
    this.reportToken = Axios.CancelToken.source();
    this.setState({ loading: pagingKey });

    const params = {
      ...this.getParams(),
      take: 20,
      page,
    };

    const sessionParams = JSON.stringify(reportFilters);
    sessionStorage.setItem(SessionStorageKey, sessionParams);
    return api.base
      .post(`${config.appUrls.reporting}/api/RegistrationsPerRelation`, params, {
        cancelToken: this.reportToken.token,
      })
      .then(({ data }) => {
        const newRows = data.rows;
        const totals = {
          rowDepth: 0,
          rowType: -1,
          rowKey: 'Totals',
          ...(data.totals || {}),
        };

        this.setState(state => ({
          ...state,
          rows: params.page > 0 ? [...state.rows, ...newRows] : [totals, ...newRows],
          totals: params.page > 0 ? state.totals : data.totals,
          hasMoreData: data.hasMoreData,
          loading: null,
        }));
        return data.rows;
      })
      .catch(error => {
        this.setState(state => ({
          loading: state.loading && state.loading === pagingKey ? null : state.loading,
          hasMoreData: state.loading && state.loading === pagingKey ? false : state.hasMoreData,
        }));
        const { intl } = this.props;
        if (!Axios.isCancel(error)) return handleHttpError(intl)(error);
      });
  };

  createExcelReport = (isDetailsReport = false) => {
    const { intl } = this.props;

    const reportParams = { ...this.getParams(), isDetailsReport };
    const languageCode = intl.locale;

    const params = {
      language: languageCode,
      signalRConnectionId: SignalRService.getConnectionId(),
      report: 4,
      parameters: JSON.stringify(reportParams),
      isDetailsReport,
    };
    this.setState({ exporting: true });
    return api.base
      .post(`${config.appUrls.reporting}/api/export`, params)
      .then(() => {
        ToastrService.info(intl.formatMessage(messages.ExportToExcelStarted));
      })
      .catch(error => {
        handleHttpError(intl)(error);
      })
      .finally(() => {
        this.setState({ exporting: false });
      });
  };

  filtersChanged(filters) {
    this.setState(state => ({
      reportFilters: {
        ...(state.reportFilters || {}),
        ...filters,
      },
      rows: [],
      hasMoreData: true,
      loading: null,
      pagingKey: state.pagingKey + 1,
    }));
  }

  showHoursChanged = value => {
    sessionStorage.setItem(ShowHoursStorageKey, value);
    this.setState({ showHours: value });
  };

  render() {
    const { intl } = this.props;
    const {
      reportFilters,
      rows,
      showHours,
      hasMoreData,
      loading,
      pagingKey,
      mainTaskTemplates,
      totals,
      exporting,
    } = this.state;

    const showDetails = reportFilters.groupBy.indexOf(99) >= 0;
    let [lastGroup] = [...reportFilters.groupBy].reverse();
    if (!lastGroup) lastGroup = 1;
    const columnStatus = getVisibleColumnsFromTotals(totals);

    return (
      <div className="registration-per-relation">
        <SimplePanel
          article="5434847027730"
          title={intl.formatMessage(messages.RegistrationsPerRelation)}
        >
          <div className="info-block container-fluid">
            <RegistrationsPerReportFilters
              filters={reportFilters}
              mainTaskTemplates={mainTaskTemplates}
              onFilterChange={filters => this.filtersChanged(filters)}
              showHours={showHours}
              onShowHoursChanged={this.showHoursChanged}
              rootGroup={1}
              reportKey="RegsPerRelationReport"
            />
          </div>
        </SimplePanel>

        <div className="row btn-row">
          <div className="col-md-6 pull-right content-right">
            {rows && rows.length > 0 && (
              <ExportToExcelDropdown
                exporting={exporting}
                createExcel={this.createExcelReport}
                intl={intl}
              />
            )}
          </div>
        </div>

        <InfiniteScroll
          pageStart={-1}
          loadMore={page => this.retrieve(page)}
          hasMore={hasMoreData && !loading}
          initialLoad
          loader={null}
          key={pagingKey}
        >
          <table className="table margin50B scrollable-table table-report grouped-report registrations-per-relation">
            <thead>
              <tr>
                <th rowSpan="2">
                  <FormattedMessage {...messages.Name} />
                </th>
                {showDetails && (
                  <th rowSpan="2">
                    <FormattedMessage {...messages.Remark} />
                  </th>
                )}
                {showDetails && columnStatus.finalInvoicePermission && (
                  <th rowSpan="2">
                    <FormattedMessage {...messages.InvoiceStatus} />
                  </th>
                )}
                {showDetails && columnStatus.priceListPermission && (
                  <th rowSpan="2" colSpan="text-center">
                    <FormattedMessage {...messages.InForfait} />
                  </th>
                )}
                <th
                  colSpan={
                    1 +
                    (columnStatus.priceListPermission ? 1 : 0) +
                    (columnStatus.internalPricePermission ? 1 : 0) +
                    (columnStatus.potentialQuantity ? 1 : 0)
                  }
                  className="text-center border-dashed"
                >
                  <FormattedMessage {...messages.Potential} />
                </th>
                {columnStatus.notBillable && (
                  <th
                    colSpan={2 + (columnStatus.notBillableQuantity ? 1 : 0)}
                    className="text-center border-dashed"
                  >
                    <FormattedMessage {...messages.NotBillable} />
                  </th>
                )}
                {columnStatus.billed && (
                  <th
                    colSpan={2 + (columnStatus.billedQuantity ? 1 : 0)}
                    className="text-center border-dashed"
                  >
                    <FormattedMessage {...messages.Invoiced} />
                  </th>
                )}
                {columnStatus.inForfait && (
                  <th
                    colSpan={2 + (columnStatus.inForfaitQuantity ? 1 : 0)}
                    className="text-center border-dashed"
                  >
                    <FormattedMessage {...messages.InvoicedInForfait} />
                  </th>
                )}
                {columnStatus.deleted && (
                  <th
                    colSpan={2 + (columnStatus.deletedQuantity ? 1 : 0)}
                    className="text-center border-dashed"
                  >
                    <FormattedMessage {...messages.Deleted} />
                  </th>
                )}
                {columnStatus.neverInvoice && (
                  <th
                    colSpan={2 + (columnStatus.neverInvoiceQuantity ? 1 : 0)}
                    className="text-center border-dashed"
                  >
                    <FormattedMessage {...messages.NeverInvoice} />
                  </th>
                )}

                {columnStatus.writtenOff && (
                  <th
                    colSpan={2 + (columnStatus.writtenOffQuantity ? 1 : 0)}
                    className="text-center border-dashed"
                  >
                    <FormattedMessage {...messages.WrittenOffInternally} />
                  </th>
                )}
                {columnStatus.billed && (
                  <th
                    colSpan={2 + (columnStatus.billedQuantity ? 1 : 0)}
                    className="text-center border-dashed"
                  >
                    <FormattedMessage {...messages.InvoicedMinusPotential} />
                  </th>
                )}
                {columnStatus.margin && (
                  <th className="text-right border-dashed">
                    <FormattedMessage {...messages.Margin} />
                  </th>
                )}
              </tr>
              <tr>
                <th className="text-right border-dashed">
                  <FormattedMessage {...messages.Hours} />
                </th>
                {columnStatus.potentialQuantity && (
                  <th className="text-right">
                    <FormattedMessage {...messages.Quantity} />
                  </th>
                )}
                {columnStatus.priceListPermission && (
                  <th className="text-right">
                    <FormattedMessage {...messages.Amount} />
                  </th>
                )}
                {columnStatus.internalPricePermission && (
                  <th className="text-right">
                    <FormattedMessage {...messages.InternalCost} />
                  </th>
                )}
                {columnStatus.notBillable && (
                  <th className="text-right border-dashed">
                    <FormattedMessage {...messages.Hours} />
                  </th>
                )}
                {columnStatus.notBillableQuantity && (
                  <th className="text-right">
                    <FormattedMessage {...messages.Quantity} />
                  </th>
                )}
                {columnStatus.notBillable && (
                  <th className="text-right">
                    <FormattedMessage {...messages.Amount} />
                  </th>
                )}
                {columnStatus.billed && (
                  <th className="text-right border-dashed">
                    <FormattedMessage {...messages.Hours} />
                  </th>
                )}
                {columnStatus.billedQuantity && (
                  <th className="text-right">
                    <FormattedMessage {...messages.Quantity} />
                  </th>
                )}
                {columnStatus.billed && (
                  <th className="text-right">
                    <FormattedMessage {...messages.Amount} />
                  </th>
                )}
                {columnStatus.inForfait && (
                  <th className="text-right border-dashed">
                    <FormattedMessage {...messages.Hours} />
                  </th>
                )}
                {columnStatus.inForfaitQuantity && (
                  <th className="text-right">
                    <FormattedMessage {...messages.Quantity} />
                  </th>
                )}
                {columnStatus.inForfait && (
                  <th className="text-right">
                    <FormattedMessage {...messages.Amount} />
                  </th>
                )}
                {columnStatus.deleted && (
                  <th className="text-right border-dashed">
                    <FormattedMessage {...messages.Hours} />
                  </th>
                )}
                {columnStatus.deletedQuantity && (
                  <th className="text-right">
                    <FormattedMessage {...messages.Quantity} />
                  </th>
                )}
                {columnStatus.deleted && (
                  <th className="text-right">
                    <FormattedMessage {...messages.Amount} />
                  </th>
                )}
                {columnStatus.neverInvoice && (
                  <th className="text-right border-dashed">
                    <FormattedMessage {...messages.Hours} />
                  </th>
                )}
                {columnStatus.neverInvoiceQuantity && (
                  <th className="text-right">
                    <FormattedMessage {...messages.Quantity} />
                  </th>
                )}
                {columnStatus.neverInvoice && (
                  <th className="text-right">
                    <FormattedMessage {...messages.Amount} />
                  </th>
                )}
                {columnStatus.writtenOff && (
                  <th className="text-right border-dashed">
                    <FormattedMessage {...messages.Hours} />
                  </th>
                )}
                {columnStatus.writtenOffQuantity && (
                  <th className="text-right">
                    <FormattedMessage {...messages.Quantity} />
                  </th>
                )}
                {columnStatus.writtenOff && (
                  <th className="text-right">
                    <FormattedMessage {...messages.Amount} />
                  </th>
                )}
                {columnStatus.billed && (
                  <th className="text-right border-dashed">
                    <FormattedMessage {...messages.Hours} />
                  </th>
                )}
                {columnStatus.billedQuantity && (
                  <th className="text-right">
                    <FormattedMessage {...messages.Quantity} />
                  </th>
                )}
                {columnStatus.billed && (
                  <th className="text-right">
                    <FormattedMessage {...messages.Amount} />
                  </th>
                )}
                {columnStatus.margin && (
                  <th className="text-right border-dashed">
                    <FormattedMessage {...messages.InvoicedMinusInternalCost} />
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {rows &&
                rows.map(row => (
                  <RegistrationsPerRow
                    key={row.rowKey}
                    rowData={row}
                    showHours={showHours}
                    showDetails={showDetails}
                    lastGroup={lastGroup}
                    columnStatus={columnStatus}
                  />
                ))}
            </tbody>
          </table>
          {loading && (
            <div>
              <LoadingAnimation />
            </div>
          )}
        </InfiniteScroll>
        <ScrollArrow />
      </div>
    );
  }
}

RegistrationsPerRelationContainer.propTypes = {
  intl: intlShape.isRequired,
  searchParams: PropTypes.shape({ relationId: PropTypes.string }),
};

RegistrationsPerRelationContainer.defaultProps = {
  searchParams: {},
};

export default showWhenPermission(
  'RegistrationOwn',
  'read',
)(injectStateService(injectIntl(RegistrationsPerRelationContainer)));
